export const DEFAULT_LANGUAGE = "de";
export const SUPPORTED_LANGUAGES = ["de", "en"];

export function isSupportedLanguage(lang) {
  return SUPPORTED_LANGUAGES.includes(lang);
}

export function getCurrentLanguage(pathname = typeof window !== "undefined" ? window.location.pathname : "/") {
  const path = pathname || "/";
  if (path === "/en" || path.startsWith("/en/")) return "en";
  return DEFAULT_LANGUAGE;
}

export function stripLanguagePrefix(path) {
  if (!path) return "/";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === "/en") return "/";
  if (normalized.startsWith("/en/")) return normalized.slice(3) || "/";
  return normalized;
}

export function getLocalizedPath(path, lang = DEFAULT_LANGUAGE) {
  const base = stripLanguagePrefix(path.startsWith("/") ? path : `/${path}`);
  if (lang === "en") return base === "/" ? "/en" : `/en${base}`;
  return base;
}

export function withLanguageParam(pathOrUrl, lang = DEFAULT_LANGUAGE) {
  const safeLang = isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE;
  if (!pathOrUrl) return `?lang=${safeLang}`;

  const isAbsolute = /^https?:\/\//i.test(pathOrUrl);
  if (isAbsolute) {
    const url = new URL(pathOrUrl);
    url.searchParams.set("lang", safeLang);
    return url.toString();
  }

  const [pathPart, queryPart = ""] = String(pathOrUrl).split("?");
  const params = new URLSearchParams(queryPart);
  params.set("lang", safeLang);
  const qs = params.toString();
  return qs ? `${pathPart}?${qs}` : `${pathPart}?lang=${safeLang}`;
}

export function logApiLanguageDebug(label, requestedLang, raw) {
  if (process.env.NODE_ENV !== "development") return;
  const current = raw?.language?.current;
  if (current == null) return;
  console.debug(`[coinarchive:${label}] requested=${requestedLang} response=${current}`);
}

export function getCoinSlugForLang(coin, lang = DEFAULT_LANGUAGE) {
  const safeLang = isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE;
  const fromMap = coin?.translations?.[safeLang];
  if (fromMap) return fromMap;
  return coin?.slug || "";
}

export function getLocalizedCoinPath(coin, lang = DEFAULT_LANGUAGE) {
  const slug = getCoinSlugForLang(coin, lang);
  if (!slug) return getLocalizedPath("/coins", lang);
  return getLocalizedPath(`/coins/${slug}`, lang);
}

export function buildCoinLanguagePaths(coin) {
  const paths = {};
  for (const code of SUPPORTED_LANGUAGES) {
    paths[code] = getLocalizedCoinPath(coin, code);
  }
  return paths;
}

export function logMissingCoinTranslationSlugs(label, requestedLang, coin) {
  if (process.env.NODE_ENV !== "development" || !coin) return;
  const t = coin.translations;
  const hasDe = Boolean(t?.de);
  const hasEn = Boolean(t?.en);
  if (hasDe && hasEn && t.de !== t.en) return;
  if (requestedLang === "en" && !hasEn) {
    console.warn(`[coinarchive:${label}] API response missing EN translation slug — language switch may keep DE slug until plugin adds translations`);
  }
  if (requestedLang === "de" && !hasDe) {
    console.warn(`[coinarchive:${label}] API response missing DE translation slug`);
  }
}
